import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api';
import { computeCrackedScore } from '../lib/crackedScore';
import {
  MOCK_LEARNING_INSIGHTS,
  MOCK_CONFUSION_LOGS,
  MOCK_STUDY_DASHBOARD,
} from '../lib/progressMock';

const USE_MOCK = process.env.REACT_APP_PROGRESS_MOCK === 'true';

const EMPTY_INSIGHTS = {
  weak_topics: [],
  confusion_patterns: [],
  strengths: [],
  insights: [],
  total_questions_answered: 0,
  total_confusion_logs: 0,
  overall_correct_rate: 0,
};

async function safeJson(fetcher) {
  try {
    return await fetcher();
  } catch {
    return null;
  }
}

/**
 * Loads Progress page data from API with optional mock overlay and graceful degradation.
 */
export function useProgressData() {
  const [insights, setInsights] = useState(null);
  const [confusionLogs, setConfusionLogs] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [lectures, setLectures] = useState(null);
  const [reviewSessions, setReviewSessions] = useState(null);
  const [studyDashboard, setStudyDashboard] = useState(null);
  const [usingMock, setUsingMock] = useState(USE_MOCK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (USE_MOCK) {
      setInsights(MOCK_LEARNING_INSIGHTS);
      setConfusionLogs(MOCK_CONFUSION_LOGS);
      setSubjects([]);
      setLectures([]);
      setReviewSessions([]);
      setStudyDashboard(MOCK_STUDY_DASHBOARD);
      setUsingMock(true);
      setLoading(false);
      return;
    }

    try {
      const [
        ins,
        logs,
        subs,
        lecs,
        reviews,
        dash,
      ] = await Promise.all([
        safeJson(() => api.getLearningInsights()),
        safeJson(() => api.getConfusionLogs(80)),
        safeJson(() => api.getSubjects()),
        safeJson(() => api.getLectures()),
        safeJson(() => api.getReviewSessions()),
        safeJson(() => api.getStudyDashboard()),
      ]);

      const insightsOk = ins != null;
      const logsOk = Array.isArray(logs);
      if (!insightsOk && !logsOk) {
        setInsights(MOCK_LEARNING_INSIGHTS);
        setConfusionLogs(MOCK_CONFUSION_LOGS);
        setUsingMock(true);
      } else {
        setInsights(insightsOk ? ins : EMPTY_INSIGHTS);
        setConfusionLogs(logsOk ? logs : []);
        setUsingMock(false);
      }
      setSubjects(subs || []);
      setLectures(lecs || []);
      setReviewSessions(reviews || []);
      setStudyDashboard(dash ?? null);
    } catch (e) {
      setError(e?.message || 'Failed to load progress');
      setInsights(MOCK_LEARNING_INSIGHTS);
      setConfusionLogs(MOCK_CONFUSION_LOGS);
      setStudyDashboard(MOCK_STUDY_DASHBOARD);
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confusionMeta = useMemo(() => {
    const logs = confusionLogs || [];
    const unresolved = logs.filter((l) => !l.resolved).length;
    return { unresolved, total: logs.length };
  }, [confusionLogs]);

  const cracked = useMemo(
    () => computeCrackedScore(insights, confusionMeta),
    [insights, confusionMeta]
  );

  return {
    loading,
    error,
    refresh: load,
    usingMock,
    insights,
    confusionLogs: confusionLogs || [],
    subjects: subjects || [],
    lectures: lectures || [],
    reviewSessions: reviewSessions || [],
    studyDashboard,
    confusionMeta,
    cracked,
  };
}
